import * as React from 'react';
import { isEmpty } from 'lodash';

import Tooltip from 'components/Tooltip';
import ActionLink from 'components/buttons/action-link/ActionLink';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import CollapsibleSimple from 'components/collapsible/simple/CollapsibleSimple';
import CollapsibleContainer from 'components/collapsible/container/CollapsibleContainer';

import CheckboxSelector from 'modules/discovery/components/checkbox-selector/CheckboxSelector';
import ControlledRangeSelect from 'modules/discovery/components/range-select/ControlledRangeSelect';
import DiscoveryFiltersTitle from 'modules/discovery/components/filters/title/DiscoveryFiltersTitle';

import { abbrNum } from 'utils/helper/common';
import { FOLLOWERS_STEP_SELECTOR, transformSteps } from 'modules/discovery/utils/discovery';
import { __t, applyTranslations } from 'utils/language';

import type {
  DataStructureCommon,
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilters,
  DiscoveryFilterMinMax
} from 'types';

import './discovery-creators-filters-instagram.scss';

applyTranslations(require('./discovery-creators-filters-instagram.i18n.json'));

type Props = {
  filters: DiscoveryCreatorsFilters,
  handleDiscoveryFiltersUpdate: (a: DiscoveryCreatorsFilter) => void
};

type State = {
  followersTiersInstagramEnabled: boolean
};

class DiscoveryCreatorsFiltersInstagram extends React.PureComponent<Props, State> {
  static defaultProps = {
    filters: {},
    handleDiscoveryFiltersUpdate: () => {}
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      followersTiersInstagramEnabled: false
    };
  }

  resetTiers(): void {
    const { followersTiersInstagramEnabled } = this.state;

    if (followersTiersInstagramEnabled) {
      return;
    }

    const { filters } = this.props;
    const instagram = filters.followers.instagram;
    const tiers = instagram.tiers?.data || [];
    const resetTiers = tiers.map(item => ({
      ...item,
      selected: false
    }));

    this.handleTiersUpdate(resetTiers);
  }

  handleUpdate = (result: DiscoveryCreatorsFilter): void => {
    const { handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate(result);
    }
  };

  handleFollowersSliderChangeComplete = (range: DiscoveryFilterMinMax) => {
    const { followers: propsFollowers } = this.props.filters;

    this.handleUpdate({
      followers: {
        ...propsFollowers,
        instagram: {
          ...propsFollowers.instagram,
          // $FlowIgnore - Cannot determine a type for object literal [1]. DiscoveryFilterMinMax [2] is inexact
          ...range,
          transformed: {
            ...transformSteps(range, FOLLOWERS_STEP_SELECTOR)
          }
        }
      }
    });
  };

  handleEngagementChangeComplete = (range: DiscoveryFilterMinMax) => {
    const { engagement: propsEngagement } = this.props.filters;

    this.handleUpdate({
      engagement: {
        ...propsEngagement,
        instagram: {
          ...propsEngagement.instagram,
          // $FlowIgnore - Cannot determine a type for object literal [1]. DiscoveryFilterMinMax [2] is inexact
          ...range
        }
      }
    });
  };

  handleTiersToggle = () => {
    this.setState((state) => ({
      followersTiersInstagramEnabled: !state.followersTiersInstagramEnabled
    }), () => this.resetTiers());
  };

  handleTiersUpdate = (data: Array<DataStructureCommon>) => {
    const { followers: propsFollowers } = this.props.filters;

    this.handleUpdate({
      followers: {
        ...propsFollowers,
        instagram: {
          ...propsFollowers.instagram,
          tiers: {
            enabled: data.some(entry => entry.selected),
            data
          }
        }
      }
    });
  };

  renderFollowersSlider(): React.ReactNode {
    const { filters } = this.props;
    const { followersTiersInstagramEnabled } = this.state;

    const valueTransformer = (range) => {
      const transformedReach = transformSteps(range, FOLLOWERS_STEP_SELECTOR);

      const valueMin = abbrNum(transformedReach.min, { decimal: 1 });
      const valueMax = abbrNum(transformedReach.max, { decimal: 1 });
      return `${valueMin} – ${valueMax}`;
    };

    return (
      <ControlledRangeSelect
        key="instagram-followers-range"
        title={__t('discovery_creators_filters_instagram.search_filters_followers_title')}
        tooltip={__t('discovery_creators_filters_instagram.search_filters_tooltip_followers')}
        titleValueTransformer={valueTransformer}
        isDisabled={followersTiersInstagramEnabled}
        data={filters.followers.instagram}
        onChangeComplete={this.handleFollowersSliderChangeComplete}
      />
    );
  }

  renderTiers(): React.ReactNode {
    const { filters } = this.props;
    const followers = filters.followers.instagram;
    const { followersTiersInstagramEnabled } = this.state;

    const message = (followersTiersInstagramEnabled)
      ? __t('discovery_creators_filters_instagram.hide_tiers_label')
      : __t('discovery_creators_filters_instagram.show_tiers_label');

    return (
      <React.Fragment>
        <ActionLink
          size="normal"
          onClick={this.handleTiersToggle}
        >
          {message}
        </ActionLink>
        <CollapsibleContainer
          isExpanded={followersTiersInstagramEnabled}
        >
          <React.Fragment>
            <VerticalSpacing type="large" />
            <DiscoveryFiltersTitle title={__t('discovery_creators_filters_instagram.search_filters_followers_tiers_title')}>
              <div className="discovery-creators-filters-instagram__tooltip-icon">
                <Tooltip
                  message={__t('discovery_creators_filters_instagram.search_filters_tooltip_followers_tiers')}
                  showQuestionMark
                />
              </div>
            </DiscoveryFiltersTitle>
            <VerticalSpacing type="medium" />
            <CheckboxSelector
              items={followers.tiers?.data || []}
              onUpdate={this.handleTiersUpdate}
              attribute="followersTiersInstagram"
              direction="column"
            />
          </React.Fragment>
        </CollapsibleContainer>
      </React.Fragment>
    );
  }

  renderEngagement(): React.ReactNode {
    const { filters } = this.props;

    const valueTransformer = (value) => {
      let minValue = String(value.min);
      if (minValue === '0.1') {
        minValue = '0';
      }

      return `${minValue}% – ${value.max}%`;
    };

    return (
      <ControlledRangeSelect
        key="instagram-followers-engagement"
        title={__t('discovery_creators_filters_instagram.search_filters_engagement_title')}
        tooltip={__t('discovery_creators_filters_instagram.search_filters_tooltip_engagement')}
        titleValueTransformer={valueTransformer}
        data={filters.engagement.instagram}
        attribute="reachInstagram"
        onChangeComplete={this.handleEngagementChangeComplete}
      />
    );
  }

  render(): React.ReactNode {
    const { filters } = this.props;
    const searchByName = filters.searchByName;

    return (
      <CollapsibleSimple
        title={__t('discovery_creators_filters_instagram.section_label')}
        contentClassName="discovery-creators-filters-instagram"
        isExpanded={isEmpty(searchByName)}
        isDisabled={!isEmpty(searchByName)}
      >
        {this.renderFollowersSlider()}
        <VerticalSpacing type="large" />
        {this.renderTiers()}
        <VerticalSpacing type="large" />
        {this.renderEngagement()}
        <VerticalSpacing type="tiny" />
      </CollapsibleSimple>
    );
  }
}

export default DiscoveryCreatorsFiltersInstagram;
